import Scene = Phaser.Scene;
import {lazyInject} from "@/container";
import {TYPES} from "@/types";
import {PlayerCreatureService} from "@/services/player-creature-service";
import {SceneName} from "@/model/scene-name";
import {SceneService} from "@/services/scene-service";
import {ShopService, TabName} from "@/services/shop-service";
import {Settings} from "@/constants/settings";
import {Keys} from "@/constants/key";
import {BasePanel} from "@/scenes.ui/base-panel";
import SceneUtils from "@/utils/scene-utils";
import {MenuService} from "@/services/menu-service";
import {ArtifactLessonService} from "@/services/artifact-lesson-service";
import {CreatureUtils} from "@/utils/creature-utils";
import {Sizes} from "@/constants/size";
import {TaskService} from "@/services/task-service";
import ListenerManager from "@/managers/listener-manager";
import {ProfileService} from "@/services/profile-service";
import {PlayerSpeedService} from "@/services/player-speed-service";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {EnemyCreatureService} from "@/services/enemy-creature-service";
import {Creature} from "@/store/level/types";
import SoundUtils from "@/utils/sound-utils";
import {ListService} from "@/services/list-service";
import PlayerStore from "@/store/player";
import Container = Phaser.GameObjects.Container;
import {PaymentChangeService} from "@/services/payment-change-service";
import {IapService} from "@/services/iap-service";
import {LoadUtils} from "@/utils/load-utils";
import {TenjinService} from "@/services/tenjin-service";
import ArtifactUtils from "@/utils/artifact-utils";
import {Reloadable} from "@/scenes/reloadable";

export default class CreatureScene extends Scene implements Reloadable {

    @lazyInject(TYPES.PlayerCreatureService)
    private playerCreatureService!: PlayerCreatureService;

    @lazyInject(TYPES.EnemyCreatureService)
    private enemyCreatureService!: EnemyCreatureService;

    @lazyInject(TYPES.SceneService)
    private sceneService!: SceneService;

    @lazyInject(TYPES.MenuService)
    private menuService!: MenuService;

    @lazyInject(TYPES.ListService)
    private listService!: ListService;

    @lazyInject(TYPES.ShopService)
    private shopService!: ShopService;

    @lazyInject(TYPES.ArtifactLessonService)
    private artifactLessonService!: ArtifactLessonService;

    @lazyInject(TYPES.TaskService)
    private taskService!: TaskService;

    @lazyInject(TYPES.ProfileService)
    private profileService!: ProfileService;

    @lazyInject(TYPES.PlayerSpeedService)
    private playerSpeedService!: PlayerSpeedService;

    @lazyInject(TYPES.PaymentNotificationService)
    private paymentNotificationService!: PaymentNotificationService;

    @lazyInject(TYPES.PaymentChangeService)
    private paymentChangeService!: PaymentChangeService;

    @lazyInject(TYPES.IapService)
    private iapService!: IapService;

    private canvas!: HTMLCanvasElement;

    private creatureIndex: number = 0;

    private openUnlockPanel!: boolean;

    private enemyProfile: boolean = false;

    private enemyCreatures!: Creature[];

    private musicKey = Keys.MUSIC_FANTASY_SNEAK;

    constructor() {
        super({ key: 'CreatureScene' });
    }

    // noinspection JSUnusedGlobalSymbols
    init(data: any)  {
        this.openUnlockPanel = data.openUnlockPanel;
        this.enemyProfile = data.enemyProfile;
        this.enemyCreatures = data.creatures;

        let player = PlayerStore.getPlayer();

        if (LoadUtils.isTheMedievalMusicInCreatureScene()) {
            this.musicKey = Keys.MUSIC_THE_MEDIEVAL;
        } else {
            this.musicKey = Keys.MUSIC_FANTASY_SNEAK;
        }

        if (this.openUnlockPanel) {
            this.creatureIndex = CreatureUtils.sortCreaturesDescending(player!.creatures)
                    .indexOf(CreatureUtils.findFirstCreatureAbleToGetArtifact(player!, Sizes.MAX_ARTIFACT_SLOTS_COUNT)!);
        } else {
            this.creatureIndex = data.creatureIndex;
        }

    }

    public preload(): void {
        SceneUtils.setCurrentScene(this);
        this.canvas = this.sys.game.canvas;
    }

    public create(): void {
        this.sceneService!.fadeBlackout(this, this.canvas!.width, this.canvas!.height);
        const self = this;

        const music = SoundUtils.addSound(this, this.musicKey, {volume: Settings.MUSIC_VOLUME});
        SoundUtils.play(music);

        SoundUtils.setLooped(music);

        if (this.enemyProfile) {

            this.enemyCreatureService.initEnemyCreatureService(this, this.canvas, this.creatureIndex, () => {
                BasePanel.closeAll();
                self.sceneService.startScene(this, SceneName.ROAD_SCENE, this.canvas.width, this.canvas.height);
                SoundUtils.stop(music);
            }, this.enemyCreatures);
        } else {
            this.playerCreatureService.initPlayerCreatureService(this, this.canvas, this.artifactLessonService, this.listService, this.creatureIndex, () => {
                BasePanel.closeAll();
                self.sceneService.startScene(this, SceneName.ROAD_SCENE, this.canvas.width, this.canvas.height);
                SoundUtils.stop(music);
            }, () => {
                BasePanel.closeAll();
                let menuContainer = this.menuService.createMenu(this, this.canvas, true, (sceneName: SceneName, params: any) => {
                        self.shopService.closeShop();
                        if (params && params.creatureIndex) {
                            self.playerCreatureService.select(params.creatureIndex);
                        }
                    }, () => self.openShop(menuContainer, TabName.BANK),
                    () => self.openShop(menuContainer, TabName.BANK),
                    () => {},
                    () => {},
                    () => {},
                    () => {},
                    () => {},
                    () => {}, () => {}, () => {}, () => {});
                this.openShop(menuContainer, TabName.BANK);
            }, this.openUnlockPanel);
        }

        this.menuService.setShowNavPanel(false);
    }

    public update(): void {
        ListenerManager.callUpdateListeners();
    }

    private openShop(menuContainer: Container, tabName: TabName) {
        let self = this;
        this.shopService.openShop(this, this.canvas, true, () => {
            self.menuService.resourceToSide();
            self.menuService.destroyMenu();
        }, self.paymentNotificationService!, (creatureId: string) => {
            self.listService.showManual(this, this.canvas, creatureId, () => {});
        }, tabName, this.listService, this.paymentChangeService, this.iapService);
        menuContainer.setDepth(4000);
        this.menuService.setShowNavPanel(true);
        self.menuService.resourceToBottom();
        this.menuService.onChange();
    }

    public reload(): void {
        this.sceneService.startSceneInstant(this, SceneName.CREATURE_SCENE, {creatureIndex: this.creatureIndex});
    }
}
